import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { conPeriodo, etiquetaHorario } from "../../../../utils/etiquetaHorario";
import EstadoCuentaModal from "../../../../components/estadoCuenta/estadoCuentaModal";

import HttpManager from "../../../../services/HttpManager";
import { RosterRow, ScheduleRosterDTO, TargetSchedule } from "../../../../models/EnrollmentUpdateDTO";
import { cursoCorto } from "../../../../utils/cursoCorto";
import BuscadorHorarios, { DetalleHorario, HorarioFila, filaDeDestino } from "../../../../components/horarios/buscadorHorarios";
import SugerenciasHorario, { tituloSugerencia } from "../../../../components/horarios/sugerenciasHorario";
import { SugerenciaHorario } from "../../../../components/horarios/sugerenciasApi";
import { dinero, errorText, tipoDe } from "./helpers";
import { Choice, Decision, ForCaja, PersonProblem, closeWithDecisions, mergeWithDecisions, problemsOf } from "./fusionarService";
import "./fusionarHorario.css";

/*
	Fusionar / Cerrar un horario con una decisión por persona (Esteban, 2026-10-29: «Fusionar = decisión por persona»).
	Cada persona de A sale con su saldo y su Estado de Cuenta; nada viene elegido y el botón se enciende cuando todas tienen
	decisión. El servidor valida todo antes de mover a nadie: si una no se puede, no se mueve nadie y cada fila dice por qué.
*/

const T = {
	mergeTitle: "Fusionar Horario",
	closeTitle: "Cerrar Horario",
	mergeInto: "Fusionar Hacia",
	reason: "Motivo",
	reasonRequired: "Motivo (obligatorio)",
	note: "Nota",
	otherSchedule: "Otro Horario",
	paused: "Pausado",
	noSchedule: "Sin Horario",
	retired: "Retiro",
	graduated: "Graduado",
	postponed: "Aplazar",
	notStarted: "No Comenzó",
	passTo: (h: string) => `Pasa a ${h}`,
	statement: "Estado de Cuenta",
	pending: (m: string) => `Pendiente ${m}`,
	overdue: (m: string) => `Vencido ${m}`,
	alCorriente: "Al Corriente",
	paidThrough: (mes: string) => `Pagado hasta ${mes}`,
	missing: (n: number) => n === 1 ? "Falta 1" : `Faltan ${n}`,
	merge: "Fusionar",
	close: "Cerrar Horario",
	cancel: "Volver",
	change: "Cambiar",
	loading: "Cargando…",
	nobody: "Sin personas activas",
	merged: (n: number) => n === 1 ? "Fusionado: 1 persona." : `Fusionado: ${n} personas.`,
	closed: (n: number) => n === 1 ? "Cerrado: 1 persona." : `Cerrado: ${n} personas.`,
	partly: (aplicadas: number, caja: string) => `${aplicadas} ${aplicadas === 1 ? "aplicada" : "aplicadas"}. Por Aplicar para Caja: ${caja}. El horario sigue Cerrando hasta que Caja las aplique.`,
	porCaja: (tipo: string, f: ForCaja) => `${tipo || "Actualización"} de ${f.studentName}`,
	yMas: (n: number) => ` y ${n} más`,
};

const etiqueta = etiquetaHorario;

/** Orden en que se enseñan las opciones (el servidor dice cuáles aplican a cada persona). */
const ORDEN_OPCIONES: Choice[] = ["target", "other", "graduated", "postponed", "paused", "no_schedule", "retired", "not_started"];
/** Si el servidor no mandó opciones (versión vieja): solo las que no dependen de fechas, sesiones ni asistencias. */
const SIN_REGLAS: { merge: Choice[]; close: Choice[] } = { merge: ["target", "other", "paused", "no_schedule", "retired"], close: ["other", "paused", "no_schedule", "retired"] };

const diasEntre = (a: string | null | undefined, b: string | null | undefined): number => {
	const x = a ? Date.parse(a.slice(0, 10)) : NaN; const y = b ? Date.parse(b.slice(0, 10)) : NaN;
	return isNaN(x) || isNaN(y) ? Number.MAX_SAFE_INTEGER : Math.abs(x - y) / 86400000;
};

/** Destinos sugeridos primero: mismo día y hora, luego mismo periodo, luego el que arrancó más cerca del origen (va en el mismo punto). */
const ordenDestinos = (lista: TargetSchedule[], origen: ScheduleRosterDTO["schedule"]): number[] => {
	const mismaHora = (t: TargetSchedule) => !!origen.sessions && !!t.sessions && t.sessions === origen.sessions ? 0 : 1;
	const mismoPeriodo = (t: TargetSchedule) => !!origen.periodName && t.periodName === origen.periodName ? 0 : 1;
	return [...lista].sort((a, b) => mismaHora(a) - mismaHora(b) || mismoPeriodo(a) - mismoPeriodo(b)
		|| diasEntre(a.startDate, origen.startDate) - diasEntre(b.startDate, origen.startDate) || a.id - b.id).map((t) => t.id);
};

/** Una sugerencia elegida se guarda como fila del buscador (lo que la decisión necesita: id, horario, periodo, maestra). */
const deSugerencia = (s: SugerenciaHorario): HorarioFila => ({
	id: s.id, plantelId: null, curso: "", modulo: s.levelName || "", periodo: s.periodName || "", horario: tituloSugerencia(s), dias: s.days || [], minuto: -1,
	inicio: (s.startDate || "").slice(0, 10), fin: (s.endDate || "").slice(0, 10), maestro: s.mentorName || "", aula: null, alumnos: s.enrolled, cupo: s.limit || null, status: ""
});

interface Props {
	campusId: number;
	countryId?: number | null;
	movil: boolean;
	mode: "merge" | "close";
	roster: ScheduleRosterDTO;
	/** Destinos del mismo curso (los de `_options`); si no vienen se piden aquí. */
	targets?: TargetSchedule[];
	onClose: () => void;
	onDone: (message: string) => void;
}

interface Row { choice: Choice | null; other: HorarioFila | null; picking: boolean; note: string; }
const EMPTY_ROW: Row = { choice: null, other: null, picking: false, note: "" };

const FusionarHorario: React.FC<Props> = ({ campusId, countryId, movil, mode, roster, targets: targetsProp, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	const isMerge = mode === "merge";
	const sch = roster.schedule;
	const people = roster.rows;
	const [cuenta, setCuenta] = useState<ScheduleRosterDTO["rows"][number] | null>(null);
	const [targets, setTargets] = useState<TargetSchedule[] | null>(targetsProp && targetsProp.length ? targetsProp : null);
	const [target, setTarget] = useState<HorarioFila | null>(null);
	const [pickingTarget, setPickingTarget] = useState<boolean>(true);
	const [reason, setReason] = useState<string>("");
	const [rows, setRows] = useState<{ [enrollmentId: number]: Row }>({});
	const [problems, setProblems] = useState<PersonProblem[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);

	// Los destinos son los mismos para todo el grupo (mismo curso): se piden con la primera persona si unHorario no los pasó.
	useEffect(() => {
		if (targets !== null || people.length === 0) { if (targets === null) { setTargets([]); } return; }
		let vigente = true;
		http.enrollmentUpdatesService.options(campusId, people[0].enrollmentId)
			.then((o) => { if (vigente) { setTargets(o.data.targetSchedules || []); } })
			.catch((e) => { if (vigente) { setTargets([]); setError(errorText(e)); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId]);

	const lista = useMemo(() => targets || [], [targets]);
	const curso = cursoCorto(sch.courseName);
	const complemento = useMemo(() => lista.map((t) => filaDeDestino(t, curso, campusId)), [lista, curso, campusId]);
	const detalle = useMemo(() => {
		const out: { [id: number]: DetalleHorario } = {};
		lista.forEach((t) => { out[t.id] = { cupo: t.limit || null, alumnos: t.enrolled, lleno: t.isFull, sesion: t.classesGiven + 1 }; });
		return out;
	}, [lista]);
	const orden = useMemo(() => ordenDestinos(lista, sch), [lista, sch]);
	const idsDestino = lista.map((t) => t.id).filter((id) => id !== sch.id);
	const idsOtro = idsDestino.filter((id) => !isMerge || id !== target?.id);
	const periodoDe = (id: number | undefined | null): string | null => (lista.find((t) => t.id === id)?.periodName) || null;
	const etiquetaFila = (h: HorarioFila | null): string => h ? conPeriodo(h.horario, h.periodo || periodoDe(h.id)) : "";

	// Solo las opciones que el servidor dice que aplican a cada persona (las que no, no se muestran: el motivo sería ruido).
	const textoOpcion = (k: Choice): string => {
		switch (k) {
			case "target": return T.passTo(target ? etiquetaFila(target) : "…");
			case "other": return T.otherSchedule;
			case "graduated": return T.graduated;
			case "postponed": return T.postponed;
			case "paused": return T.paused;
			case "no_schedule": return T.noSchedule;
			case "retired": return T.retired;
			default: return T.notStarted;
		}
	};
	const choicesFor = (r: RosterRow): { k: Choice; t: string }[] => {
		const permitidas = (r.choices ? (isMerge ? r.choices.merge : r.choices.close) : (isMerge ? SIN_REGLAS.merge : SIN_REGLAS.close)) as string[];
		return ORDEN_OPCIONES.filter((k) => permitidas.includes(k)).map((k) => ({ k, t: textoOpcion(k) }));
	};

	const row = (id: number): Row => rows[id] || EMPTY_ROW;
	const setRow = (id: number, patch: Partial<Row>) => { setRows((x) => ({ ...x, [id]: { ...(x[id] || EMPTY_ROW), ...patch } })); setProblems((p) => p.filter((q) => q.enrollmentId !== id)); };
	const decided = (r: RosterRow): boolean => {
		const x = row(r.enrollmentId);
		if (!x.choice) { return false; }
		if (x.choice === "other") { return !!x.other; }
		if (x.choice === "target") { return !!target; }
		return true;
	};
	const faltan = people.filter((r) => !decided(r)).length;
	const listo = !busy && faltan === 0 && (!isMerge || !!target) && (isMerge || reason.trim().length >= 3);

	// Tipo como en el resto del módulo (tipoDe): un cambio a otro periodo se llama «Cambio de Periodo».
	const porCajaTexto = (f: ForCaja): string => {
		const x = row(f.enrollmentId);
		const destino = x.choice === "other" ? x.other : x.choice === "target" ? target : null;
		const aPeriodo = destino ? (destino.periodo || periodoDe(destino.id)) : null;
		return T.porCaja(tipoDe({ typeId: f.typeId, typeLabel: f.typeLabel, fromSchedule: { periodName: sch.periodName }, toSchedule: aPeriodo ? { periodName: aPeriodo } : null }), f);
	};

	const enviar = () => {
		const decisions: Decision[] = people.map((r) => {
			const x = row(r.enrollmentId);
			return { enrollmentId: r.enrollmentId, choice: x.choice as Choice, toScheduleId: x.choice === "other" ? x.other?.id : undefined, note: x.note.trim() || undefined };
		});
		setBusy(true); setError(null); setProblems([]);
		const req = isMerge ? mergeWithDecisions(campusId, sch.id, target!.id, reason.trim(), decisions) : closeWithDecisions(campusId, sch.id, reason.trim(), decisions);
		req.then((res) => {
			const r = res.data.result;
			const total = people.length;
			if (r && r.pendingForCaja > 0) {
				const lista = r.forCaja || [];
				const caja = lista.length ? lista.slice(0, 3).map(porCajaTexto).join(", ") + (lista.length > 3 ? T.yMas(lista.length - 3) : "") : String(r.pendingForCaja);
				onDone(T.partly(r.applied, caja));
				return;
			}
			onDone(isMerge ? T.merged(total) : T.closed(total));
		}).catch((e) => {
			const p = problemsOf(e);
			setProblems(p);
			setError(errorText(e));
		}).finally(() => setBusy(false));
	};

	const problemaDe = (id: number) => problems.filter((p) => p.enrollmentId === id);
	const generales = problems.filter((p) => p.enrollmentId === null);

	const saldo = (r: RosterRow) => (
		<span className="fus-saldo">
			{r.overdue > 0 ? <Badge bg="oro">{T.overdue(dinero(r.overdue, countryId))}</Badge> : null}
			<Badge bg="secondary">{r.debt > 0 ? T.pending(dinero(r.debt, countryId)) : T.alCorriente}</Badge>
			{r.paidThrough ? <span className="wau-sec fus-pagado">{T.paidThrough(r.paidThrough)}</span> : null}
		</span>
	);

	return (
		<div className="wau-modal-fondo" role="dialog" aria-modal="true" aria-label={isMerge ? T.mergeTitle : T.closeTitle}>
			<div className={"wau-modal ancho fus" + (movil ? " movil" : "")} onClick={(e) => e.stopPropagation()}>
				<h3>{isMerge ? T.mergeTitle : T.closeTitle}</h3>
				<div className="wau-destino fus-origen"><span><b>{curso ? curso + ", " : ""}{etiqueta(sch.name, sch.periodName)}</b>{sch.levelName ? ", " + sch.levelName : ""}{sch.mentorName ? ", " + sch.mentorName : ""}</span></div>

				{isMerge ? (
					<div className="wau-pila fus-bloque">
						<span className="wau-lab">{T.mergeInto}</span>
						{targets === null ? <span className="wau-sec">{T.loading}</span> : target && !pickingTarget ? (
							<div className="wau-destino"><span><b>{etiquetaFila(target)}</b>{target.modulo ? ", " + target.modulo : ""}{target.maestro ? ", " + target.maestro : ""}</span>
								<button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => setPickingTarget(true)}>{T.change}</button></div>
						) : (
							<BuscadorHorarios compacto campusId={campusId} soloIds={idsDestino} orden={orden} complemento={complemento} detalle={detalle} seleccionado={target?.id}
								onElegir={(h) => { setTarget(h); setPickingTarget(false); setProblems([]); }} />
						)}
					</div>
				) : null}

				<textarea className="wau-entrada fus-bloque" rows={2} placeholder={isMerge ? T.reason : T.reasonRequired} aria-label={T.reason} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />

				{generales.length ? <div className="wau-aviso mal">{generales.map((p, i) => <span key={"g" + i}>{p.message} </span>)}</div> : null}

				<div className="fus-lista">
					{people.length === 0 ? <div className="wau-sec">{T.nobody}</div> : people.map((r) => {
						const x = row(r.enrollmentId);
						const mal = problemaDe(r.enrollmentId);
						return (
							<div key={"fp" + r.enrollmentId} className={"wau-tarjeta fus-persona" + (mal.length ? " mal" : decided(r) ? " lista" : "")}>
								<div className="fus-cabeza">
									<b className="fus-nombre">{r.studentName}</b>
									{saldo(r)}
									<button type="button" className="btn btn-link btn-sm fus-ec" onClick={() => setCuenta(r)}>{T.statement}</button>
								</div>
								<div className="wau-chips fus-chips" role="group" aria-label={r.studentName || ""}>
									{choicesFor(r).map((c) => (
										<button key={c.k} type="button" className={"waFmBoton" + (x.choice === c.k ? " activo" : "")} aria-pressed={x.choice === c.k} disabled={busy || (c.k === "target" && !target)}
											onClick={() => setRow(r.enrollmentId, { choice: c.k, picking: c.k === "other" && !x.other })}>{c.t}</button>
									))}
								</div>
								{x.choice === "other" ? (
									x.other && !x.picking ? (
										<div className="wau-destino"><span><b>{etiquetaFila(x.other)}</b>{x.other.maestro ? ", " + x.other.maestro : ""}</span>
											<button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => setRow(r.enrollmentId, { picking: true })}>{T.change}</button></div>
									) : (
										<div className="wau-pila">
											<SugerenciasHorario campusId={campusId} enrollmentId={r.enrollmentId} mode="merge" excluirIds={[sch.id, ...(target ? [target.id] : [])]} seleccionado={x.other?.id} limite={3}
												onElegir={(s) => setRow(r.enrollmentId, { other: deSugerencia(s), picking: false })} />
											<BuscadorHorarios compacto campusId={campusId} soloIds={idsOtro} orden={orden} complemento={complemento} detalle={detalle} seleccionado={x.other?.id}
												onElegir={(h) => setRow(r.enrollmentId, { other: h, picking: false })} />
										</div>
									)
								) : null}
								{x.choice ? (
									<input className="wau-entrada fus-nota" placeholder={T.note} aria-label={T.note + " " + r.studentName} value={x.note} disabled={busy}
										onChange={(e) => setRow(r.enrollmentId, { note: e.target.value })} />
								) : null}
								{mal.map((p, i) => <div key={"m" + i} className="fus-error" role="alert">{p.message}</div>)}
							</div>
						);
					})}
				</div>

				{error && problems.length === 0 ? <div className="wau-aviso mal" role="alert"><span>{error}</span></div> : null}

				<div className="wau-acciones fus-acciones">
					{faltan > 0 ? <Badge bg="oro">{T.missing(faltan)}</Badge> : null}
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={onClose}>{T.cancel}</button>
					<button type="button" className="btn btn-primary btn-sm rounded-pill px-3" disabled={!listo} onClick={enviar}>{isMerge ? T.merge : T.close}</button>
				</div>
			</div>
			{cuenta ? <EstadoCuentaModal campusId={campusId} studentId={cuenta.studentId} studentName={cuenta.studentName} enrollmentId={cuenta.enrollmentId} onClose={() => setCuenta(null)} /> : null}
		</div>
	);
};

export default FusionarHorario;
